/**
 * RefinanceIntelligenceRouter — mode-aware dispatch for /reports/refinance-intelligence.
 *
 * Reads `companyData.loanBookMode` from outlet context and renders either
 *   • the legacy `RefinanceIntelligencePage` (Entourage etc.), OR
 *   • the new `RefinanceIntelligenceEntityPage` (Timms, Intuitive Finance,
 *     all future unified-mode tenants) which sources the picker from the
 *     `loan_books` subcollection.
 *
 * NOTE: the legacy page was "SHA frozen / byte-identical" until Phase 3c
 * (2026-06-22), which added the canonical per-loan contact-logging surface
 * (<ContactTimeline>) to both pages. Behaviour otherwise unchanged; the two
 * pages still differ only in how they source the loan-book picker.
 *
 * AppLayout already gates `<Outlet>` on `userLoading || companyLoading`, so
 * `companyData` is non-null by the time this router mounts. The explicit null
 * guard is belt-and-braces against future loading-state regressions.
 */

import React from 'react';
import { useOutletContext } from 'react-router-dom';
import RefinanceIntelligencePage from './RefinanceIntelligencePage';
import RefinanceIntelligenceEntityPage from './RefinanceIntelligenceEntityPage';

const RefinanceIntelligenceRouter: React.FC = () => {
  const ctx = useOutletContext<{ companyData?: { loanBookMode?: string } | null } | undefined>();
  const companyData = ctx?.companyData;

  if (!companyData) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-400">Loading company configuration...</p>
      </div>
    );
  }

  return companyData.loanBookMode === 'unified'
    ? <RefinanceIntelligenceEntityPage />
    : <RefinanceIntelligencePage />;
};

export default RefinanceIntelligenceRouter;
